import React, { useState } from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { TripRiskScoresDto } from '@acorn-portal/shared/src/services/telematicsDriverTripsApi';
import TripScoreBadge from './trip-score-badge';
import TripScoreRow from './trip-score-row';
import { normalizeScore } from './trip-utils';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';

/**
 * Props for the TripScoreBreakdown component.
 */
interface TripScoreBreakdownProps {
	/** Risk scores data from the telematics API. */
	riskScores?: TripRiskScoresDto;
	/** Whether to show numeric scores and the trip score badge. Bars and tips still render when false. Default: false */
	showScores?: boolean;
}

/**
 * Displays a detailed breakdown of trip scores by category.
 *
 * Shows the total trip score with a badge and individual scores for
 * smoothness, speed, and usage categories. Each category includes a
 * progress bar and optional description from the API. Normalizes scores
 * from the API's -10 to 10 range to 0-100 for display.
 *
 * @component
 * @example
 * ```tsx
 * <TripScoreBreakdown riskScores={tripDetails.riskScores} />
 * ```
 */
const TripScoreBreakdown: React.FC<TripScoreBreakdownProps> = ({ riskScores, showScores = false }) => {
	const theme = useTheme();
	const [infoModalOpen, setInfoModalOpen] = useState(false);
	const title = showScores ? 'Score Breakdown' : 'Breakdown';

	if (!riskScores) {
		return (
			<Box
				sx={{
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: 2,
					backgroundColor: theme.palette.grey[100],
					p: 2,
				}}
			>
				<Typography variant='body2' color='text.secondary'>
					{showScores ? 'Score breakdown not available.' : 'Breakdown not available.'}
				</Typography>
			</Box>
		);
	}

	const totalScore = normalizeScore(riskScores.totalScore ?? 0);
	const smoothnessScore = normalizeScore(riskScores.smoothScore ?? 0);
	const speedScore = normalizeScore(riskScores.speedScore ?? 0);
	const usageScore = normalizeScore(riskScores.useScore ?? 0);

	return (
		<Box
			sx={{
				border: `1px solid ${theme.palette.divider}`,
				borderRadius: 2,
				backgroundColor: theme.palette.grey[100],
				overflow: 'hidden',
			}}
		>
			{/* Header */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					p: 2,
					borderBottom: `1px solid ${theme.palette.divider}`,
				}}
			>
				<Typography
					variant='subtitle1'
					sx={{
						fontWeight: 'bold',
						color: theme.palette.text.primary,
					}}
				>
					{title}
				</Typography>
				<IconButton
					size='small'
					onClick={() => setInfoModalOpen(true)}
					sx={{ p: 0 }}
					aria-label={showScores ? 'Score breakdown information' : 'Breakdown information'}
				>
					<InfoIcon
						sx={{
							fontSize: 20,
							color: theme.palette.success.main,
						}}
					/>
				</IconButton>
			</Box>

			{/* Info Modal */}
			<HelperModal
				open={infoModalOpen}
				onClose={() => setInfoModalOpen(false)}
				title={title}
				description='Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.<br/><br/>Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.'
			/>

			{/* Content */}
			<Box sx={{ p: 2 }}>
				{/* Trip Score with Badge */}
				{showScores && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							mb: 3,
						}}
					>
						<Typography
							variant='body1'
							sx={{
								fontWeight: 'medium',
								color: theme.palette.text.primary,
							}}
						>
							Trip score
						</Typography>
						<TripScoreBadge score={totalScore} />
					</Box>
				)}

				{/* Individual Score Categories - using descriptions from API */}
				<TripScoreRow
					label='Smoothness'
					score={smoothnessScore}
					description={riskScores.smoothScoreDescription}
					showScore={showScores}
				/>

				<TripScoreRow
					label='Speed'
					score={speedScore}
					description={riskScores.speedScoreDescription}
					showScore={showScores}
				/>

				<TripScoreRow
					label='Usage'
					score={usageScore}
					description={riskScores.useScoreDescription}
					showScore={showScores}
				/>
			</Box>
		</Box>
	);
};

export default TripScoreBreakdown;
